<template>
  <aside
    class="hidden lg:block sticky border-r border-gray-200 bg-white p-5 antialiased text-fg h-body top-(--header-height) overflow-y-auto z-31"
    :class="isSidebarCollapsed ? 'w-22' : 'w-64'"
  >
    <div
      class="flex flex-col h-full"
      :class="isSidebarCollapsed ? 'gap-4' : 'gap-7'"
    >
      <div>
        <div
          class="flex items-center gap-2"
          :class="isSidebarCollapsed ? 'justify-center' : 'justify-between'"
        >
          <h2
            v-if="!isSidebarCollapsed"
            class="text-xl font-bold text-gray-900 flex-1 min-w-0 break-words"
          >
            {{ event?.name }}
          </h2>

          <div class="flex flex-col items-end gap-3 shrink-0">
            <Tooltip
              :description="
                $t(
                  !isSidebarCollapsed
                    ? 'sidebar.toggle.close.tooltip'
                    : 'sidebar.toggle.open.tooltip',
                )
              "
              :placement="!isSidebarCollapsed ? 'top' : 'right'"
            >
              <button
                class="hidden lg:flex p-2 rounded-lg w-full hover:bg-gray-100"
                @click="emit('toggle-sidebar', !isSidebarCollapsed)"
              >
                <FontAwesomeIcon
                  :icon="['far', !isSidebarCollapsed ? 'xmark' : 'sidebar']"
                  class="text-lg"
                />
              </button>
            </Tooltip>
          </div>
        </div>
        <p v-if="!isSidebarCollapsed" class="text-gray-500 text-sm mt-1">
          {{ customFormat(event?.starts_at, 'MMMM dd, yyyy') }}
        </p>
        <!-- <span
          v-if="!isSidebarCollapsed"
          class="block w-max mt-2 text-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800"
          >Free</span
        > -->
      </div>

      <ul class="flex-1 flex flex-col gap-1">
        <li
          v-for="menu in activeMenuElements"
          :key="menu.label"
          class="relative flex items-center"
          :class="[
            isSidebarCollapsed && 'justify-center',
            menu.coming_soon && 'pointer-events-none',
          ]"
        >
          <Tooltip
            v-if="!menu.dropdown"
            class="justify-start w-full"
            :tooltip-class="!isSidebarCollapsed ? 'hidden' : ''"
            :description="$t(`common.${menu.label}`)"
            width="w-max"
            placement="right"
          >
            <component
              :is="menu.newTab ? 'a' : Link"
              class="group flex items-center gap-2 p-2 rounded-lg w-full hover:bg-gray-100 font-medium"
              :class="[
                isMenuActive(menu) ? 'text-primary bg-gray-100' : '',
                isSidebarCollapsed && 'justify-center',
              ]"
              :href="
                useRoute(menu.route, {
                  event: event?.id,
                  ...(menu.entryParams ?? {}),
                })
              "
              :target="menu.newTab ? '_blank' : undefined"
              :rel="menu.newTab ? 'noopener noreferrer' : undefined"
            >
              <FontAwesomeIcon
                v-if="menu.icon"
                :icon="menu.icon"
                class="w-4"
                :class="menu.coming_soon && 'opacity-50'"
              />
              <span
                v-if="!isSidebarCollapsed"
                class="whitespace-nowrap text-sm"
                :class="menu.coming_soon && 'opacity-50'"
                v-text="$t(`common.${menu.label}`)"
              />
              <FontAwesomeIcon
                v-if="!isSidebarCollapsed && menu.newTab"
                :icon="['far', 'arrow-up-right-from-square']"
                class="w-3 text-gray-400"
              />
              <span
                v-if="!isSidebarCollapsed && menu.coming_soon"
                class="whitespace-nowrap text-micro uppercase text-white bg-purple-300 rounded-lg px-1.5 py-0.5 text-center w-max"
                v-text="$t('common.coming_soon')"
              />
            </component>
          </Tooltip>

          <GroupedItems v-else :menu="menu" />
        </li>
      </ul>

      <div class="flex-1 flex flex-col justify-end">
        <Tooltip
          class="justify-start"
          :tooltip-class="!isSidebarCollapsed ? 'hidden' : ''"
          :description="$t('common.my_events')"
          width="w-max"
          placement="right"
        >
          <Link
            class="group flex items-center gap-3 p-2 rounded-lg w-full hover:bg-gray-100"
            :class="isSidebarCollapsed && 'justify-center'"
            :href="useRoute('dashboard')"
          >
            <FontAwesomeIcon :icon="['fas', 'arrow-left']" class="w-4" />
            <span
              v-if="!isSidebarCollapsed"
              class="whitespace-nowrap text-sm"
              v-text="$t('common.my_events')"
            />
          </Link>
        </Tooltip>
      </div>
    </div>
  </aside>
</template>

<script setup>
import { inject, computed } from 'vue'
import { Link, usePage } from '@inertiajs/vue3'
import { useVisibleMenuElements } from '@/composables/navigationPreferences'
import { useRoute } from '@/composables/route'
import { customFormat } from '@/composables/date'
import Tooltip from '@/Components/common/Tooltip.vue'
import GroupedItems from '@/Components/Sidebar/GroupedItems.vue'

const emit = defineEmits(['toggle-sidebar'])

const isSidebarCollapsed = inject('isSidebarCollapsed')

const event = computed(() => usePage().props.event)
const activeMenuElements = computed(() => useVisibleMenuElements())

const isMenuActive = (menu) => {
  const component = usePage().component
  return (
    component === menu?.name ||
    (menu?.activeComponents ?? []).includes(component)
  )
}
</script>
